import { apiConfig, buildApiUrl, getProjectsUrl, getProjectTicketsUrl, getScheduledResourcesUrl, getWorkRolesUrl, getUsersByWorkRoleIdsUrl, getProjectRolesUrl, getAddMemberToProjectUrl, getPermissionsUrl, getPaycorTimeOffUrl, getServiceBoardStatusUrl } from "../apiConfig";

// Build a ConnectWise-style conditions expression like: field in ("a","b") or field in (1,2)
export function buildInCondition(field, values, quoteStrings = true) {
  const safe = (values || []).filter(v => v !== undefined && v !== null);
  if (safe.length === 0) return "";
  const formatted = safe
    .map(v => (quoteStrings && typeof v === "string" ? `"${v}"` : String(v)))
    .join(",");
  return `${field} in (${formatted})`;
}

// Build a simple comparison condition, with value auto-quoted
export function buildCompareCondition(field, op, value) {
  const v = typeof value === "string" ? `"${value}"` : String(value);
  return `${field} ${op} ${v}`;
}

async function fetchJson(url, token) {
  const headers = { "Accept": "application/json" };
  if (token) {
    headers["Authorization"] = token; // intentionally no Bearer prefix
  }
  // Do NOT send cookies by default to avoid CORS 'credentials' requirements
  // If your API needs cookies, set credentials: 'include' and ensure server sends
  // Access-Control-Allow-Credentials: true and a specific Access-Control-Allow-Origin.
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`Request failed ${res.status}: ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  // Some endpoints may return empty body
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  return res.json();
}

// Fetch JSON but also return response headers for pagination
async function fetchJsonWithHeaders(url, token) {
  const headers = { "Accept": "application/json" };
  if (token) {
    headers["Authorization"] = token;
  }
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`Request failed ${res.status}: ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  const contentType = res.headers.get("content-type") || "";
  let data = null;
  if (contentType.includes("application/json")) {
    data = await res.json();
  }
  return { data, headers: res.headers };
}

function toArrayFromResponse(result) {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  if (result && Array.isArray(result.items)) return result.items;
  if (result && Array.isArray(result.value)) return result.value; // some APIs wrap in value
  return [];
}

function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// API: Fetch projects by allowed status/name values
export async function fetchProjectsByStatuses(statusNames, token) {
  const conditions = buildInCondition("status/name", statusNames, true);
  const url = getProjectsUrl(conditions);
  return fetchJson(url, token);
}

// API: Fetch projects filtered by status and a start/end date window
export async function fetchProjectsByStatusesInDateWindow(statusNames, startIso, endIso, token) {
  // Single request with status filter and inclusive OR date window using brackets per requirement:
  // Updated to one-year bounded AND condition as requested:
  // status/name in ("a","b",...) and estimatedStart >= [startIso] and estimatedEnd <= [endIso]
  const quotedStatuses = statusNames || [];
  const statusCond = buildInCondition("status/name", quotedStatuses, true);
  const parts = [statusCond];
  if (startIso) parts.push(`estimatedStart >= [${startIso}]`);
  const conditions = parts.filter(Boolean).join(" and ");
  const url = getProjectsUrl(conditions);
  const res = await fetchJson(url, token);
  return toArrayFromResponse(res);
}

// API: Fetch tickets by project IDs
export async function fetchTicketsByProjectIds(projectIds, token, options = {}) {
  const ids = (projectIds || []).filter(Boolean);
  if (!ids.length) return [];
  // Smaller batchSize to avoid 1000-row server cap per query
  const batchSize = options.batchSize || 15; // default lowered from 50
  const chunks = chunkArray(ids, batchSize);
  const all = [];
  // Optional date window for tickets: scheduleStartDate within [dateStart, dateEnd)
  const dateStartIso = options.dateStart || null;
  const dateEndIso = options.dateEnd || null;
  for (const chunk of chunks) {
    const baseCond = buildInCondition("project/id", chunk, false);
    const parts = [baseCond];
    if (dateStartIso) parts.push(`scheduleStartDate >= [${dateStartIso}]`);
    if (dateEndIso) parts.push(`scheduleStartDate < [${dateEndIso}]`);
    const conditions = parts.filter(Boolean).join(" and ");
    let page = 1;
    const pageSize = options.pageSize || 200;
    let more = true;
    while (more) {
      const url = getProjectTicketsUrl(conditions, { page, pageSize });
      const res = await fetchJson(url, token);
      const arr = toArrayFromResponse(res);
      all.push(...arr);
      if (typeof window !== 'undefined') {
        console.log('[fetchTicketsByProjectIds] chunk', chunk, 'page', page, 'received', arr.length, 'totalAccum', all.length);
      }
      const totalPages = res?.totalPages ?? res?.pageCount ?? null;
      if (totalPages && page < totalPages) {
        page += 1;
      } else if (arr.length === pageSize) {
        page += 1;
      } else {
        more = false;
      }
      if (page > 100) more = false; // safety cap
    }
  }
  return all;
}

// API: Fetch tickets in a date window using scheduleStartDate, following "nextpage" header pagination
export async function fetchTicketsByDateWindow(startIso, endIso, token, options = {}) {
  const parts = [];
  if (startIso) parts.push(`scheduleStartDate >= [${startIso}]`);
  if (endIso) parts.push(`scheduleStartDate <= [${endIso}]`);
  const conditions = parts.filter(Boolean).join(" and ");
  let page = options.pageStart || 1;
  const pageSize = options.pageSize || 200;
  const all = [];
  let hasNext = true;
  while (hasNext) {
    const url = getProjectTicketsUrl(conditions, { page, pageSize });
    const { data, headers } = await fetchJsonWithHeaders(url, token);
    const arr = toArrayFromResponse(data);
    all.push(...arr);
    // Robust pagination detection: support headers and payload fallbacks
    const nextHeader = headers.get("nextpage");
    const nextVal = typeof nextHeader === 'string' ? nextHeader.trim().toLowerCase() : String(nextHeader || '').toLowerCase();
    const xNextPage = headers.get("x-next-page") || headers.get("next-page") || null;
    const linkHeader = headers.get("link") || headers.get("Link") || null;
    const xTotalPages = headers.get("x-total-pages") || headers.get("x-page-count") || null;
    const totalPagesField = (typeof data === 'object') ? (data?.totalPages ?? data?.pageCount ?? null) : null;

    // Determine next page number if available
    let nextPageNumber = null;
    if (nextVal === 'true' || nextVal === '1') {
      nextPageNumber = page + 1;
    } else if (xNextPage) {
      const n = Number(String(xNextPage).trim());
      if (!Number.isNaN(n) && n > page) {
        nextPageNumber = n;
      } else {
        // Header present but not numeric; assume sequential
        nextPageNumber = page + 1;
      }
    } else if (typeof linkHeader === 'string' && /rel="?next"?/i.test(linkHeader)) {
      nextPageNumber = page + 1;
    } else if (xTotalPages && !Number.isNaN(Number(xTotalPages)) && page < Number(xTotalPages)) {
      nextPageNumber = page + 1;
    } else if (typeof totalPagesField === 'number' && page < totalPagesField) {
      nextPageNumber = page + 1;
    } else if (arr.length === pageSize) {
      // Fallback heuristic: full page implies more results
      nextPageNumber = page + 1;
    }

    hasNext = nextPageNumber != null;
    if (hasNext) page = nextPageNumber;
    // Safety cap to avoid infinite loops if header misbehaves
    if (page > 100) break;
  }
  return all;
}

// API: Fetch scheduled resources by ticket IDs (objectId references ticket)
export async function fetchScheduledResourcesByTicketIds(ticketIds, token, options = {}) {
  const ids = (ticketIds || []).filter(v => v !== undefined && v !== null);
  if (!ids.length) return [];
  const batchSize = options.batchSize || 50;
  const pageSize = options.pageSize || 200;
  const chunks = chunkArray(ids, batchSize);
  const collected = [];
  for (const chunk of chunks) {
    // Try multiple field names that may reference the ticket.
    // Some scheduled resource records use objectId, others ticket/id or ticketId.
    const fieldVariants = ["objectId", "ticket/id", "ticketId"]; // expand if needed
    for (const field of fieldVariants) {
      const cond = buildInCondition(field, chunk, false);
      let page = 1;
      let more = true;
      while (more) {
        const url = getScheduledResourcesUrl(cond, { page, pageSize });
        const res = await fetchJson(url, token).catch(e => { console.error('[fetchScheduledResourcesByTicketIds] failed', field, e); return null; });
        const arr = toArrayFromResponse(res);
        if (arr.length) {
          collected.push(...arr);
        }
        const totalPages = res?.totalPages ?? res?.pageCount ?? null;
        if (totalPages && page < totalPages) {
          page += 1;
        } else if (arr.length === pageSize) {
          page += 1; // assume more pages
        } else {
          more = false;
        }
        if (page > 100) more = false;
      }
    }
  }
  // Deduplicate by id
  const seen = new Map();
  for (const sr of collected) {
    const key = sr?.id ?? sr?.identifier ?? sr?.value ?? Math.random();
    if (!seen.has(key)) seen.set(key, sr);
  }
  console.log('[fetchScheduledResourcesByTicketIds] fetched', seen.size, 'scheduled resources for', ids.length, 'ticket ids');
  return Array.from(seen.values());
}

// API: Fetch scheduled resources in a date window using dateStart with robust pagination
export async function fetchScheduledResourcesByDateWindow(startIso, endIso, token, options = {}) {
  const parts = [];
  // Ensure we only pull scheduled (type S) resources
  parts.push('type/identifier = "S"');
  // Only add doneFlag filter when explicitly requested
  if (options.doneFlag !== undefined) {
    parts.push(`doneFlag = ${options.doneFlag === true ? 'true' : 'false'}`);
  }
  if (endIso) parts.push(`dateStart <= [${endIso}]`);
  if (startIso) parts.push(`dateEnd >= [${startIso}]`);
  // Filter by member identifiers if provided
  if (options.memberIdentifiers && options.memberIdentifiers.length > 0) {
    parts.push(buildInCondition("member/identifier", options.memberIdentifiers, true));
  }
  const conditions = parts.filter(Boolean).join(" and ");
  let page = options.pageStart || 1;
  const pageSize = options.pageSize || 200;
  const all = [];
  let hasNext = true;
  while (hasNext) {
    const url = getScheduledResourcesUrl(conditions, { page, pageSize });
    const { data, headers } = await fetchJsonWithHeaders(url, token);
    const arr = toArrayFromResponse(data);
    all.push(...arr);
    const nextHeader = headers.get("nextpage");
    const nextVal = typeof nextHeader === 'string' ? nextHeader.trim().toLowerCase() : String(nextHeader || '').toLowerCase();
    const xNextPage = headers.get("x-next-page") || headers.get("next-page") || null;
    const linkHeader = headers.get("link") || headers.get("Link") || null;
    const xTotalPages = headers.get("x-total-pages") || headers.get("x-page-count") || null;
    const totalPagesField = (typeof data === 'object') ? (data?.totalPages ?? data?.pageCount ?? null) : null;
    let nextPageNumber = null;
    if (nextVal === 'true' || nextVal === '1') {
      nextPageNumber = page + 1;
    } else if (xNextPage) {
      const n = Number(String(xNextPage).trim());
      if (!Number.isNaN(n) && n > page) {
        nextPageNumber = n;
      } else {
        nextPageNumber = page + 1;
      }
    } else if (typeof linkHeader === 'string' && /rel="?next"?/i.test(linkHeader)) {
      nextPageNumber = page + 1;
    } else if (xTotalPages && !Number.isNaN(Number(xTotalPages)) && page < Number(xTotalPages)) {
      nextPageNumber = page + 1;
    } else if (typeof totalPagesField === 'number' && page < totalPagesField) {
      nextPageNumber = page + 1;
    } else if (arr.length === pageSize) {
      nextPageNumber = page + 1;
    }
    hasNext = nextPageNumber != null;
    if (hasNext) page = nextPageNumber;
    if (page > 100) break;
  }
  return all;
}

// API: Fetch tickets directly by IDs using "id in (...)" with pagination
export async function fetchTicketsByIds(ticketIds, token, options = {}) {
  const ids = (ticketIds || []).filter(v => v !== undefined && v !== null);
  if (!ids.length) return [];
  const batchSize = options.batchSize || 50;
  const pageSize = options.pageSize || 200;
  const chunks = chunkArray(ids, batchSize);
  const all = [];
  for (const chunk of chunks) {
    const conditions = buildInCondition("id", chunk, false);
    let page = options.pageStart || 1;
    let hasNext = true;
    while (hasNext) {
      const url = getProjectTicketsUrl(conditions, { page, pageSize });
      const { data, headers } = await fetchJsonWithHeaders(url, token);
      const arr = toArrayFromResponse(data);
      all.push(...arr);
      const nextHeader = headers.get("nextpage");
      const nextVal = typeof nextHeader === 'string' ? nextHeader.trim().toLowerCase() : String(nextHeader || '').toLowerCase();
      const xNextPage = headers.get("x-next-page") || headers.get("next-page") || null;
      const linkHeader = headers.get("link") || headers.get("Link") || null;
      const xTotalPages = headers.get("x-total-pages") || headers.get("x-page-count") || null;
      const totalPagesField = (typeof data === 'object') ? (data?.totalPages ?? data?.pageCount ?? null) : null;
      let nextPageNumber = null;
      if (nextVal === 'true' || nextVal === '1') {
        nextPageNumber = page + 1;
      } else if (xNextPage) {
        const n = Number(String(xNextPage).trim());
        if (!Number.isNaN(n) && n > page) {
          nextPageNumber = n;
        } else {
          nextPageNumber = page + 1;
        }
      } else if (typeof linkHeader === 'string' && /rel="?next"?/i.test(linkHeader)) {
        nextPageNumber = page + 1;
      } else if (xTotalPages && !Number.isNaN(Number(xTotalPages)) && page < Number(xTotalPages)) {
        nextPageNumber = page + 1;
      } else if (typeof totalPagesField === 'number' && page < totalPagesField) {
        nextPageNumber = page + 1;
      } else if (arr.length === pageSize) {
        nextPageNumber = page + 1;
      }
      hasNext = nextPageNumber != null;
      if (hasNext) page = nextPageNumber;
      if (page > 100) break;
    }
  }
  return all;
}

// API: Fetch work roles (optionally with custom conditions override)
export async function fetchWorkRoles(token, conditionsOverride) {
  const url = conditionsOverride ? getWorkRolesUrl(conditionsOverride) : getWorkRolesUrl();
  return fetchJson(url, token);
}

// API: Fetch users by work role IDs
export async function fetchUsersByWorkRoleIds(workRoleIds, token) {
  const ids = (workRoleIds || []).filter(id => id !== undefined && id !== null);
  const url = getUsersByWorkRoleIdsUrl(ids);
  return fetchJson(url, token);
}

// API: Fetch project roles (for assigning members to a project)
export async function fetchProjectRoles(token) {
  const url = getProjectRolesUrl();
  return fetchJson(url, token);
}

// API: Add a member to a project with a specific project role
export async function postAddMemberToProject(projectId, payload, token) {
  if (!projectId) throw new Error("postAddMemberToProject: projectId is required");
  const url = getAddMemberToProjectUrl(projectId);
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`POST failed ${res.status}: ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  return res.json();
}

// API: Fetch permissions payload
export async function fetchPermissions(token) {
  const url = getPermissionsUrl();
  return fetchJson(url, token);
}

// API: Fetch Paycor time-off requests within a date window.
// conditions format: start_time >= [YYYY-MM-DD] AND end_time <= [YYYY-MM-DD]
export async function fetchPaycorTimeOff(startDateStr, endDateStr, token) {
  const parts = [];
  if (startDateStr) parts.push(`start_time >= [${startDateStr}]`);
  if (endDateStr) parts.push(`end_time <= [${endDateStr}]`);
  parts.push('status IN [approved,pending]');
  const conditions = parts.filter(Boolean).join(' AND ');
  const url = getPaycorTimeOffUrl(conditions);
  const res = await fetchJson(url, token);
  return toArrayFromResponse(res);
}

function toIsoUtc(value) {
  const d = value instanceof Date ? value : new Date(value);
  // Return without milliseconds to match backend expectations
  const iso = d.toISOString();
  return iso.slice(0, 19) + 'Z';
}

// API: Patch a scheduled resource by id using JSON Patch operations
export async function patchScheduledResource(id, operations, token) {
  if (!id) throw new Error("patchScheduledResource: id is required");
  const base = String(apiConfig.baseUrl).replace(/\/$/, "");
  const url = `${base}${apiConfig.cwScheduledResourcesPath}/${id}`;
  const res = await fetch(url, {
    method: "PATCH",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json-patch+json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(operations || []),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`PATCH failed ${res.status}: ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  return res.json();
}

export function buildPatchForDates({ start, end }) {
  const ops = [];
  if (start) ops.push({ op: "replace", path: "dateStart", value: toIsoUtc(start) });
  if (end) ops.push({ op: "replace", path: "dateEnd", value: toIsoUtc(end) });
  return ops;
}

// Fetch all non-done scheduled resources for a given ticket id
export async function fetchScheduledResourcesByTicket(ticketId, token) {
  if (ticketId == null) throw new Error("fetchScheduledResourcesByTicket: ticketId is required");
  const conditions = `objectid in (${ticketId}) and doneFlag = false`;
  const url = getScheduledResourcesUrl(conditions);
  return fetchJson(url, token);
}

// API: Create a new scheduled resource (POST)
export async function postScheduledResource(payload, token) {
  const base = String(apiConfig.baseUrl).replace(/\/$/, "");
  const url = `${base}${apiConfig.cwScheduledResourcesPath}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const trimmed = text.trim();
    const displayMessage = (trimmed && !trimmed.startsWith('<'))
      ? trimmed
      : `POST failed ${res.status}: ${res.statusText}`;
    const err = new Error(displayMessage);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  return res.json();
}

// Helpers to safely read varying API shapes
export async function fetchServiceBoardStatuses(boardId, token) {
  const url = getServiceBoardStatusUrl(boardId);
  return fetchJson(url, token);
}

// API: Patch a ticket (e.g. update status)
export async function patchTicket(ticketId, payload, token) {
  if (!ticketId) throw new Error("patchTicket: ticketId is required");
  const base = String(apiConfig.baseUrl).replace(/\/$/, "");
  const url = `${base}${apiConfig.cwTicketBasePath}/${ticketId}`;
  const res = await fetch(url, {
    method: "PATCH",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`PATCH failed ${res.status}: ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  return res.json();
}

export function getResourceName(sr) {
  return (
    sr?.resource?.name ||
    sr?.member?.name ||
    sr?.resourceName ||
    sr?.name ||
    (sr?.resourceId ? `Resource ${sr.resourceId}` : "Unknown resource")
  );
}

export function getTicketId(sr) {
  return sr?.objectId || sr?.ticketId || sr?.id || null;
}

export function getProjectIdFromTicket(t) {
  // Accept multiple shapes:
  // - { project: { id } } or nested: { project: { id: { id } } }
  // - { projectId: number } or { projectId: { id } }
  // - { project: number }
  // - { "project/id": number|string|{ id } }
  const project = t?.project;
  // Read id from project object: id or alternative keys
  let projectObjId = project?.id ?? project?.identifier ?? project?.value ?? project?.projectId ?? null;
  if (projectObjId && typeof projectObjId === "object") {
    projectObjId = projectObjId.id ?? projectObjId.identifier ?? projectObjId.value ?? null;
  }
  const projectNum = typeof project === "number" ? project : null;
  const projectIdNum = typeof t?.projectId === "number" ? t.projectId : null;
  let projectIdObj = null;
  if (t?.projectId && typeof t.projectId === "object") {
    projectIdObj = t.projectId.id ?? t.projectId.identifier ?? t.projectId.value ?? null;
  }
  let projectSlash = null;
  if (t && Object.prototype.hasOwnProperty.call(t, "project/id")) {
    const v = t["project/id"];
    projectSlash = (typeof v === "number" || typeof v === "string") ? v : (v?.id ?? v?.identifier ?? v?.value ?? null);
  }
  return (
    projectObjId ??
    projectIdNum ??
    projectNum ??
    projectIdObj ??
    projectSlash ??
    null
  );
}

// Try to extract a human-readable ticket summary/subject/title
export function getTicketSummary(t) {
  return (
    t?.summary ||
    t?.subject ||
    t?.title ||
    t?.name ||
    t?.ticketSummary ||
    null
  );
}

export function getDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  // Numeric epoch handling (seconds vs millis)
  if (typeof value === "number") {
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string") {
    const s = value.trim();
    // For any ISO date with explicit UTC indicator (Z or +00:00), extract the date portion
    // and create local midnight to prevent off-by-one day in timezones west of UTC.
    // This handles "2026-03-23T00:00:00Z", "2026-03-23T00:00:00.0000000Z",
    // "2026-03-23T00:00:00+00:00", and any other UTC time-of-day variation.
    const utcIso = /^(\d{4})-(\d{2})-(\d{2})T[\d:.]+(?:Z|\+00:00)$/i.exec(s);
    if (utcIso) {
      const y = Number(utcIso[1]);
      const mo = Number(utcIso[2]) - 1;
      const dnum = Number(utcIso[3]);
      const localMidnight = new Date(y, mo, dnum, 0, 0, 0, 0);
      return isNaN(localMidnight.getTime()) ? null : localMidnight;
    }
    // Numeric string
    if (/^\d+$/.test(s)) {
      const num = Number(s);
      const ms = num < 1e12 ? num * 1000 : num;
      const dNum = new Date(ms);
      return isNaN(dNum.getTime()) ? null : dNum;
    }
    // Date-only string YYYY-MM-DD -> treat as local midnight
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const dOnly = new Date(`${s}T00:00:00`);
      return isNaN(dOnly.getTime()) ? null : dOnly;
    }
  }
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  // Treat sentinel/placeholder dates (e.g., 0001-01-01, 1900-01-01) as missing
  if (d.getFullYear() < 1970) return null;
  return d;
}

export function pickStart(sr) {
  return (
    getDate(sr?.start) ||
    getDate(sr?.startDate) ||
    getDate(sr?.begin) ||
    getDate(sr?.scheduledStart) ||
    getDate(sr?.dateStart) ||
    null
  );
}

export function pickEnd(sr) {
  return (
    getDate(sr?.end) ||
    getDate(sr?.endDate) ||
    getDate(sr?.finish) ||
    getDate(sr?.scheduledEnd) ||
    getDate(sr?.dateEnd) ||
    null
  );
}

// ── Soft Schedule API ────────────────────────────────────────────────────────

// GET /v1/rm_soft_schedule?dateStart=<iso>&dateEnd=<iso>
export async function fetchSoftSchedules(dateStartIso, token, dateEndIso) {
  const params = {};
  if (dateStartIso) params.dateStart = dateStartIso;
  if (dateEndIso) params.dateEnd = dateEndIso;
  const url = buildApiUrl(apiConfig.rmSoftSchedulePath, params);
  const res = await fetchJson(url, token);
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.items)) return res.items;
  return [];
}

// POST /v1/rm_soft_schedule
export async function createSoftSchedule(payload, token) {
  const url = buildApiUrl(apiConfig.rmSoftSchedulePath);
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`POST soft schedule failed ${res.status}: ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  return res.json();
}

// PUT /v1/rm_soft_schedule/<id>
export async function updateSoftSchedule(id, patch, token) {
  const url = buildApiUrl(`${apiConfig.rmSoftSchedulePath}/${id}`);
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(patch || {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`PUT soft schedule failed ${res.status}: ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  return res.json();
}

// DELETE /v1/rm_soft_schedule/<id>
export async function deleteSoftSchedule(id, token) {
  const url = buildApiUrl(`${apiConfig.rmSoftSchedulePath}/${id}`);
  const res = await fetch(url, {
    method: "DELETE",
    headers: {
      "Accept": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`DELETE soft schedule failed ${res.status}: ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  return null;
}
